import React, { useState } from "react";
import { TranslationIssues } from "./types";

interface IssuesPanelProps {
  lang: string;
  issues: TranslationIssues;
  onAddMissing: (lang: string) => Promise<any>;
  onDeleteNonMaster?: (lang: string) => Promise<any>;
  onSelectKey: (key: string) => void;
}

const IssuesPanel: React.FC<IssuesPanelProps> = ({
  lang,
  issues,
  onAddMissing,
  onDeleteNonMaster,
  onSelectKey,
}) => {
  const [isAddingMissing, setIsAddingMissing] = useState(false);
  const [isDeletingNonMaster, setIsDeletingNonMaster] = useState(false);
  const { missingKeys, emptyValues, nonMasterKeys = [] } = issues;
  const hasMissingKeys = missingKeys.length > 0;
  const hasEmptyValues = emptyValues.length > 0;
  const hasNonMasterKeys = nonMasterKeys.length > 0;
  const hasIssues = hasMissingKeys || hasEmptyValues || hasNonMasterKeys;

  const handleDeleteNonMaster = async () => {
    if (
      onDeleteNonMaster &&
      window.confirm(
        `Are you sure you want to delete ${nonMasterKeys.length} keys that don't exist in the master file?`
      )
    ) {
      setIsDeletingNonMaster(true);
      try {
        await onDeleteNonMaster(lang);
      } finally {
        setIsDeletingNonMaster(false);
      }
    }
  };

  const handleAddMissing = async () => {
    setIsAddingMissing(true);
    try {
      await onAddMissing(lang);
    } finally {
      setIsAddingMissing(false);
    }
  };

  if (!hasIssues) {
    return (
      <div className="bg-green-50 border border-green-300 rounded-lg p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-green-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm leading-5 font-medium text-green-800">
              All Good!
            </h3>
            <div className="mt-2 text-sm leading-5 text-green-700">
              <p>
                {lang.toUpperCase()} translations are complete. No missing keys
                or empty values.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-6">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg
            className="h-5 w-5 text-amber-400"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm leading-5 font-medium text-amber-800">
            Translation Issues Found
          </h3>

          {hasMissingKeys && (
            <div className="mt-2">
              <p className="text-sm leading-5 text-amber-700">
                Missing keys: {missingKeys.length}
              </p>
              <ul className="mt-1 text-xs text-amber-600 space-y-1 max-h-32 overflow-y-auto">
                {missingKeys.map((key) => (
                  <li
                    key={key}
                    className="cursor-pointer hover:underline"
                    onClick={() => onSelectKey(key)}
                  >
                    {key}
                  </li>
                ))}
              </ul>
              <button
                onClick={handleAddMissing}
                disabled={isAddingMissing}
                className={`mt-2 px-3 py-1 text-sm border rounded-md ${
                  isAddingMissing
                    ? "bg-amber-50 text-amber-500 border-amber-200 cursor-not-allowed"
                    : "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200"
                }`}
              >
                {isAddingMissing ? "Adding..." : "Add All Missing Keys"}
              </button>
            </div>
          )}

          {hasEmptyValues && (
            <div className="mt-3">
              <p className="text-sm leading-5 text-amber-700">
                Empty values: {emptyValues.length}
              </p>
              <ul className="mt-1 text-xs text-amber-600 space-y-1 max-h-32 overflow-y-auto">
                {emptyValues.map((key) => (
                  <li
                    key={key}
                    className="cursor-pointer hover:underline"
                    onClick={() => onSelectKey(key)}
                  >
                    {key}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {hasNonMasterKeys && onDeleteNonMaster && (
            <div className="mt-3">
              <p className="text-sm leading-5 text-purple-700">
                Keys not in master: {nonMasterKeys.length}
              </p>
              <ul className="mt-1 text-xs text-purple-600 space-y-1 max-h-32 overflow-y-auto">
                {nonMasterKeys.map((key) => (
                  <li
                    key={key}
                    className="cursor-pointer hover:underline"
                    onClick={() => onSelectKey(key)}
                  >
                    {key}
                  </li>
                ))}
              </ul>
              <button
                onClick={handleDeleteNonMaster}
                disabled={isDeletingNonMaster}
                className={`mt-2 px-3 py-1 text-sm border rounded-md ${
                  isDeletingNonMaster
                    ? "bg-purple-50 text-purple-400 border-purple-200 cursor-not-allowed"
                    : "bg-purple-100 text-purple-800 border-purple-300 hover:bg-purple-200"
                }`}
              >
                {isDeletingNonMaster
                  ? "Deleting..."
                  : "Delete All Non-Master Keys"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IssuesPanel;
